import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import CinematicImage from "@/components/CinematicImage";
import useContent from "@/lib/useContent";

/**
 * PageHero — supports two signatures (backward compatible):
 *   Legacy: { eyebrow, title, subtitle, image }
 *   New:    { eyebrow, kicker, title, copy, bg, primaryCta, secondaryCta }
 *
 * Pass `cmsPage` to layer CMS overrides on top of the defaults.
 */
export default function PageHero(props) {
  if (props.cmsPage) {
    return <CmsAwarePageHero {...props} />;
  }
  return <RawPageHero {...props} />;
}

function CmsAwarePageHero({ cmsPage, ...props }) {
  const { get } = useContent(cmsPage);
  const merged = {
    ...props,
    eyebrow: get("hero.eyebrow", props.eyebrow),
    title: get("hero.title", typeof props.title === "string" ? props.title : undefined) || props.title,
    subtitle: get("hero.copy", props.subtitle),
    copy: get("hero.copy", props.copy),
    image: get("hero.image", props.image),
    bg: get("hero.image", props.bg),
    primaryCta: props.primaryCta
      ? {
          ...props.primaryCta,
          label: get("hero.primaryCtaLabel", props.primaryCta.label),
          to: get("hero.primaryCtaHref", props.primaryCta.to),
        }
      : undefined,
    secondaryCta: props.secondaryCta
      ? {
          ...props.secondaryCta,
          label: get("hero.secondaryCtaLabel", props.secondaryCta.label),
          to: get("hero.secondaryCtaHref", props.secondaryCta.to),
        }
      : undefined,
  };
  return <RawPageHero {...merged} />;
}

function RawPageHero({
  eyebrow,
  kicker,
  title,
  subtitle,
  copy,
  image,
  bg,
  align = "left",
  primaryCta,
  secondaryCta,
  focalMobile = "50% 30%",
  focalDesktop = "center",
  testId,
}) {
  const heroImage = bg || image;
  const heroCopy = copy || subtitle;

  return (
    <section
      data-testid={testId || "page-hero"}
      className="relative min-h-[70vh] flex items-end overflow-hidden"
    >
      <CinematicImage
        src={heroImage}
        mobile={focalMobile}
        desktop={focalDesktop}
        scale={1.05}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent" />

      <div className="relative max-w-[1400px] mx-auto w-full px-5 sm:px-8 lg:px-12 pt-44 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className={align === "center" ? "max-w-3xl mx-auto text-center" : "max-w-3xl"}
        >
          {eyebrow && (
            <p className="tok-eyebrow mb-6" data-testid="page-hero-eyebrow">
              {eyebrow}
            </p>
          )}
          {kicker && (
            <p
              className="text-[11px] sm:text-xs uppercase tracking-[0.35em] text-amber-400/80 font-bold mb-4"
              data-testid="page-hero-kicker"
            >
              {kicker}
            </p>
          )}
          <h1
            className="tok-headline-lg"
            data-testid="page-hero-title"
          >
            {title}
          </h1>
          {heroCopy && (
            <p className="mt-6 text-[15px] sm:text-lg md:text-xl text-zinc-300 leading-[1.7] max-w-2xl">
              {heroCopy}
            </p>
          )}

          {(primaryCta || secondaryCta) && (
            <div className="mt-10 flex flex-wrap gap-3">
              {primaryCta && (
                <CTA
                  to={primaryCta.to}
                  label={primaryCta.label}
                  variant="primary"
                  testId="page-hero-primary-cta"
                />
              )}
              {secondaryCta && (
                <CTA
                  to={secondaryCta.to}
                  label={secondaryCta.label}
                  variant="ghost"
                  testId="page-hero-secondary-cta"
                />
              )}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}

function CTA({ to, label, variant, testId }) {
  const isHash = typeof to === "string" && to.startsWith("#");
  const className =
    variant === "primary"
      ? "inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-7 py-4 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
      : "inline-flex items-center gap-3 border border-amber-400/40 hover:border-amber-400 text-amber-400 px-7 py-4 font-bold uppercase tracking-[0.25em] text-xs transition-colors";

  if (isHash) {
    return (
      <a href={to} data-testid={testId} className={className}>
        {label}
        <ArrowRight className="h-4 w-4" />
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testId} className={className}>
      {label}
      <ArrowRight className="h-4 w-4" />
    </Link>
  );
}
